"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { LinkField } from "@prismicio/client";
import { Locales } from "@/lib/i18n";
import Link from "next/link";
import { getPermalink } from "@/lib/i18n/functions";
import { usePathname } from "next/navigation";

type CategoriesProps = {
  locale: Locales;
  className?: string;
  categories: {
    label: string | undefined;
    link: LinkField & { type?: string; slug?: string; uid?: string };
  }[];
};

export const Categories = ({ locale, className, categories }: CategoriesProps) => {
  const pathname = usePathname();
  return (
    <ScrollArea id="categories-block" className="max-w-full border-b">
      <nav className={cn("relative container w-full bg-white px-[25px]", className)}>
        <ul className="flex items-center gap-9 overflow-x-auto overflow-y-hidden">
          {categories.map((category, index) => {
            const isAlwaysPink = category.link.type === "tag";

            const { type, uid } = category.link;
            const segment = type === "tag" ? "tag" : "categories";
            const href = `${getPermalink(locale, segment)}/${uid}`;

            const isSelected = pathname === href;

            const textColorClass = isSelected
              ? "text-[#0093d0] font-bold"
              : isAlwaysPink
                ? "text-[#ee4197] font-semibold"
                : "text-gray-500 font-medium";

            return (
              <li key={index} className="flex-shrink-0">
                <Link
                  key={index}
                  href={href}
                  className={`relative my-5 block text-sm leading-[21px] tracking-[0] whitespace-nowrap ${textColorClass}`}
                >
                  {category.label}
                  {isSelected && <div className="absolute -bottom-5 left-0 h-0.5 w-full bg-[#0093d0]" />}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};
